import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BookingFlow,
  ManageBooking,
  resolveLabels,
  type BookingMetadata,
  type BookingPublic,
  type Labels,
  type Prefill,
} from '@zeitwahl/react';
import '@zeitwahl/react/styles.css';

// Props embedded by the server-rendered shell (api-contract.md § 7) as JSON in #zw-props.
interface ShellProps {
  apiBaseUrl?: string;
  org?: string;
  eventType?: string;
  locale?: string;
  prefill?: Prefill;
  metadata?: BookingMetadata;
  /** `{ accent: '#123' }` or `{ '--zw-accent': '#123' }`; applied as custom properties on #zw-root. */
  theme?: Record<string, string>;
  embed?: boolean;
  mode?: 'book' | 'manage';
  token?: string;
  hideEventTypeDetails?: boolean;
  initialDate?: string;
  initialMonth?: string;
  host?: string;
  poweredBy?: boolean;
  labels?: Partial<Labels>;
}

/**
 * Messages posted to the embedding page (only when `embed` is true), consumed by @zeitwahl/embed:
 *  - `zeitwahl:height`        after every size change (ResizeObserver) so the iframe can grow
 *  - `zeitwahl:date_selected` when the visitor picks a day
 *  - `zeitwahl:booked`        after a confirmed booking (no personal data, just the reference)
 */
type EmbedMessage =
  | { type: 'zeitwahl:height'; height: number }
  | { type: 'zeitwahl:date_selected'; date: string }
  | {
      type: 'zeitwahl:booked';
      booking: {
        id: string;
        publicRef: string;
        start: string;
        end: string;
        timeZone: string;
        eventType: { slug: string; title: string };
      };
    };

const THEME_NAME = /^--zw-[a-z-]+$/;
const UNSAFE_VALUE = /[;{}<>]|url\(|expression\(/i;

function readProps(): ShellProps | null {
  const el = document.getElementById('zw-props');
  if (!el?.textContent) return null;
  try {
    const parsed: unknown = JSON.parse(el.textContent);
    return parsed && typeof parsed === 'object' ? (parsed as ShellProps) : null;
  } catch {
    return null;
  }
}

function applyTheme(root: HTMLElement, theme: Record<string, string> | undefined): void {
  if (!theme) return;
  for (const [key, value] of Object.entries(theme)) {
    if (typeof value !== 'string' || value.length > 300 || UNSAFE_VALUE.test(value)) continue;
    const name = key.startsWith('--') ? key : `--zw-${key.replace(/_/g, '-')}`;
    if (THEME_NAME.test(name)) root.style.setProperty(name, value);
  }
}

function referrerOrigin(): string | null {
  try {
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    return null;
  }
}

function createMessenger(enabled: boolean): (message: EmbedMessage) => void {
  const target = enabled && window.parent !== window ? window.parent : null;
  const origin = referrerOrigin() ?? '*';
  return (message) => target?.postMessage(message, origin);
}

function observeHeight(root: HTMLElement, post: (message: EmbedMessage) => void): void {
  const send = () =>
    post({
      type: 'zeitwahl:height',
      height: Math.ceil(root.getBoundingClientRect().bottom + window.scrollY),
    });
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(send).observe(root);
  window.addEventListener('load', send);
  send();
}

function summarize(
  booking: BookingPublic
): Extract<EmbedMessage, { type: 'zeitwahl:booked' }>['booking'] {
  return {
    id: booking.id,
    publicRef: booking.publicRef,
    start: booking.start,
    end: booking.end,
    timeZone: booking.timeZone,
    eventType: { slug: booking.eventType.slug, title: booking.eventType.title },
  };
}

function PoweredBy({ labels }: { labels: Labels }) {
  const [before = '', after = ''] = labels.poweredBy.split('{name}');
  return (
    <p className="zw-root zw-powered">
      {before}
      <a href="https://zeitwahl.app" target="_blank" rel="noreferrer">
        Zeitwahl
      </a>
      {after}
    </p>
  );
}

function switchLocale(next: string): void {
  // Keeps `?token` (and every other param) so the manage page reloads in the new language.
  const url = new URL(window.location.href);
  url.searchParams.set('locale', next);
  window.location.assign(url.toString());
}

function mount(): void {
  const props = readProps();
  const root = document.getElementById('zw-root');
  if (!props || !root) return;

  applyTheme(root, props.theme);
  const embed = props.embed === true;
  const post = createMessenger(embed);
  const locale = props.locale ?? 'en';
  const apiBaseUrl = props.apiBaseUrl ?? '';
  const metadata: BookingMetadata = { ...props.metadata };
  if (embed && !metadata.embed_origin) {
    const origin = referrerOrigin();
    if (origin) metadata.embed_origin = origin;
  }

  let app: ReactNode = null;
  if (props.mode === 'manage' && props.token) {
    app = (
      <ManageBooking
        apiBaseUrl={apiBaseUrl}
        token={props.token}
        locale={locale}
        labels={props.labels}
        onLocaleChange={switchLocale}
      />
    );
  } else if (props.org && props.eventType) {
    app = (
      <BookingFlow
        apiBaseUrl={apiBaseUrl}
        org={props.org}
        eventType={props.eventType}
        locale={locale}
        prefill={props.prefill}
        metadata={metadata}
        labels={props.labels}
        hideEventTypeDetails={props.hideEventTypeDetails}
        initialDate={props.initialDate}
        initialMonth={props.initialMonth}
        host={props.host}
        onDateSelected={(date) => post({ type: 'zeitwahl:date_selected', date })}
        onBooked={(booking) => post({ type: 'zeitwahl:booked', booking: summarize(booking) })}
      />
    );
  }
  if (!app) return;

  createRoot(root).render(
    <StrictMode>
      {app}
      {props.poweredBy && <PoweredBy labels={resolveLabels(locale, props.labels)} />}
    </StrictMode>
  );
  if (embed) observeHeight(root, post);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
else mount();
